'use client';

import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Phone, 
  Calendar, 
  Star, 
  Mail, 
  FileText, 
  Plus, 
  MoreVertical, 
  MessageSquare,
  Check,
  Edit2
} from 'lucide-react';
import { ClientRecord } from './ClientsTable';

interface ClientDetailViewProps {
  client: ClientRecord;
  onBack: () => void;
  onUpdateClient: (clientId: string, data: any) => Promise<void>;
}

export default function ClientDetailView({
  client,
  onBack,
  onUpdateClient
}: ClientDetailViewProps) {
  const [activeSubProfile, setActiveSubProfile] = useState<string>('MAIN');
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteText, setNoteText] = useState(client.notes || '');
  const [historyAppointments, setHistoryAppointments] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Cargar historial completo de citas para este cliente
  useEffect(() => {
    const fetchHistory = async () => {
      setLoadingHistory(true);
      try {
        const res = await fetch(`/api/bookings?phone=${client.phone}`);
        const data = await res.json();
        if (data.success) {
          setHistoryAppointments(data.appointments || []);
        }
      } catch (err) {
        console.error('Error fetching client history:', err);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchHistory();
  }, [client.phone]);

  const handleSaveNote = async () => {
    await onUpdateClient(client.id, { notes: noteText });
    setIsEditingNote(false);
  };

  const initials = client.name.slice(0, 2).toUpperCase();

  const openWhatsApp = () => {
    const cleanPhone = client.phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/549${cleanPhone}`, '_blank');
  };

  return (
    <div className="space-y-5 max-w-6xl pb-16">
      {/* Botón de retroceso */}
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Clientes</span>
        </button>
      </div>

      {/* Cabecera de la Ficha 360° */}
      <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Avatar Grande */}
            <div className="w-16 h-16 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center font-black text-xl shadow-inner shrink-0">
              {initials}
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">
                  {client.name}
                </h2>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                  activo
                </span>
                <button className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200 transition">
                  + Etiqueta
                </button>
              </div>

              {/* Fila de Contacto */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-2 font-medium">
                <span className="flex items-center gap-1 text-slate-700 font-bold font-mono">
                  <Phone className="w-3.5 h-3.5 text-emerald-600" />
                  +549{client.phone}
                </span>

                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  Última visita: {client.appointmentsCount > 0 ? '13 oct 2026' : 'Aún no vino'}
                </span>

                <span className="flex items-center gap-1 text-amber-600">
                  <Star className="w-3.5 h-3.5" />
                  {client.points > 0 ? `${client.points} puntos` : 'Sin puntos'}
                </span>

                <span className="text-emerald-700 font-semibold cursor-pointer hover:underline">
                  {client.email ? client.email : '+ Completar email y DNI'}
                </span>
              </div>
            </div>
          </div>

          {/* Botones WhatsApp y Opciones */}
          <div className="flex items-center gap-2 self-end sm:self-center">
            <button
              onClick={openWhatsApp}
              className="w-10 h-10 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center shadow-sm transition"
              title="Abrir chat de WhatsApp"
            >
              <MessageSquare className="w-5 h-5 fill-white stroke-none" />
            </button>
            <button
              className="w-10 h-10 rounded-full bg-white border border-slate-200 hover:border-slate-300 text-slate-600 flex items-center justify-center transition shadow-sm"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Selector de Subperfiles (Familiares) */}
        <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
          <button
            onClick={() => setActiveSubProfile('MAIN')}
            className={`px-3 py-1 rounded-xl text-xs font-bold transition flex items-center gap-1 ${
              activeSubProfile === 'MAIN'
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-300'
                : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            <Check className="w-3 h-3 text-emerald-700" />
            <span>{client.name.split(' ')[0]}</span>
          </button>

          {client.subProfiles?.map((sp) => (
            <button
              key={sp.id}
              onClick={() => setActiveSubProfile(sp.id)}
              className={`px-3 py-1 rounded-xl text-xs font-bold transition ${
                activeSubProfile === sp.id
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-300'
                  : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              {sp.name}
            </button>
          ))}

          <button
            onClick={() => alert('Para añadir familiares y sub-perfiles, configure en el portal público o en citas.')}
            className="px-2.5 py-1 rounded-xl text-xs font-semibold text-slate-400 hover:text-slate-700 transition"
          >
            + PERFIL
          </button>
        </div>
      </div>

      {/* Tarjeta de Nota del Cliente */}
      <div className="bg-white border border-slate-200/90 rounded-3xl p-5 shadow-sm">
        {isEditingNote ? (
          <div className="space-y-3">
            <textarea
              rows={3}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Escribe notas clínicas, estilo preferido o preferencias del cliente..."
              className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-3 text-xs text-slate-800 focus:outline-none focus:border-emerald-500"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsEditingNote(false)}
                className="px-3 py-1.5 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveNote}
                className="px-4 py-1.5 bg-[#047857] text-white text-xs font-bold rounded-xl shadow-sm"
              >
                Guardar Nota
              </button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => setIsEditingNote(true)}
            className="border-2 border-dashed border-slate-200 hover:border-slate-300 rounded-2xl p-4 text-center cursor-pointer transition"
          >
            {client.notes ? (
              <div className="text-left text-xs text-slate-700 flex justify-between items-start">
                <p className="italic">"{client.notes}"</p>
                <Edit2 className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-2" />
              </div>
            ) : (
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-center gap-1.5">
                <Plus className="w-4 h-4" />
                <span>Agregar Nota</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Métricas Agendado y Total Cobrado */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200/90 rounded-3xl p-5 shadow-sm">
          <div className="text-xs font-bold tracking-wider text-slate-400 uppercase">
            Agendado
          </div>
          <div className="text-3xl font-extrabold text-slate-900 mt-2">
            {historyAppointments.length}
          </div>
        </div>

        <div className="bg-white border border-emerald-200/80 rounded-3xl p-5 shadow-sm bg-emerald-50/10">
          <div className="text-xs font-bold tracking-wider text-[#0e442f] uppercase">
            Total Cobrado
          </div>
          <div className="text-3xl font-extrabold text-[#047857] mt-2">
            ${client.totalSpent.toLocaleString('es-AR')}
          </div>
          <div className="w-full bg-slate-100 h-1.5 rounded-full mt-3 overflow-hidden">
            <div className="bg-[#047857] h-full rounded-full" style={{ width: '100%' }} />
          </div>
        </div>
      </div>

      {/* Tabla de Historial Contable Completo */}
      <div className="bg-white border border-slate-200/90 rounded-3xl overflow-hidden shadow-sm">
        <div className="p-4 px-6 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">
            Historial de Turnos ({historyAppointments.length})
          </h3>
          <span className="text-xs text-slate-400 font-mono">Trazabilidad en tiempo real</span>
        </div>

        {loadingHistory ? (
          <div className="p-12 text-center text-xs text-slate-400">Cargando citas...</div>
        ) : historyAppointments.length === 0 ? (
          <div className="p-12 text-center text-xs text-slate-400 italic">
            No registra citas históricas todavía.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-600">
              <thead className="bg-slate-50/80 border-b border-slate-200/80 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                <tr>
                  <th className="py-3 px-4">Fecha</th>
                  <th className="py-3 px-4">Servicio</th>
                  <th className="py-3 px-4">Profesional</th>
                  <th className="py-3 px-4">Estado</th>
                  <th className="py-3 px-4 text-right">Lista</th>
                  <th className="py-3 px-4 text-right">Efectivo</th>
                  <th className="py-3 px-4 text-right">Seña</th>
                  <th className="py-3 px-4 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {historyAppointments.map((appt) => (
                  <tr key={appt.id} className="hover:bg-slate-50/60 transition font-mono">
                    <td className="py-3.5 px-4 text-slate-700">
                      {appt.dateStr} · {appt.startTime} hs
                    </td>
                    <td className="py-3.5 px-4 font-sans font-bold text-slate-900">
                      {appt.serviceName}
                    </td>
                    <td className="py-3.5 px-4 font-sans text-slate-700">
                      {appt.staffName}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        {appt.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right text-slate-400">
                      ${appt.price?.toLocaleString('es-AR')}
                    </td>
                    <td className="py-3.5 px-4 text-right text-slate-700 font-bold">
                      ${appt.price?.toLocaleString('es-AR')}
                    </td>
                    <td className="py-3.5 px-4 text-right text-emerald-700 font-semibold">
                      ${(appt.depositAmount || 0).toLocaleString('es-AR')}
                    </td>
                    <td className="py-3.5 px-4 text-right font-black text-slate-900">
                      ${appt.price?.toLocaleString('es-AR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
